/**
 * StaffUtilizationReportPage
 *
 * Utilization per staff member -- now real actual-hours-based
 * utilization (TRS `/post-times`, chargeable client entries), not the
 * scheduled-hours proxy this page used before TRS was confirmed ready.
 * Staff identity/position/branch and capacity are real (SSS `/staff`).
 * Capacity = working weekdays in the current month x 8 hrs/day (no
 * holiday calendar wired, so this slightly overstates true capacity).
 * Falls back to sample data (src/data/sampleStaffUtilizationData.js) if
 * the real SSS fetch errors or returns nothing; if SSS is fine but TRS
 * specifically is unreachable, falls back to the scheduled-hours proxy
 * for that one page load rather than losing the whole page.
 *
 * Status/Division filters here filter by the engagement(s) each staff
 * member worked on -- not an attribute of the staff record itself -- so
 * they're implemented via a schedules/time-entries -> engagements join:
 * only rows whose engagement matches the filter count toward that staff
 * member's hours (scheduled AND actual).
 */
import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Gauge, TrendingDown, TrendingUp } from 'lucide-react';
import { useBranchContext } from '../hooks/useBranchContext.js';
import {
  useStaffList,
  useEngagements,
  useSchedules,
  scheduleStaffId,
  scheduleHours,
  scheduleEngagementId,
  countWorkingDays,
} from '../hooks/useReferenceData.js';
import { useTimeEntries, timeEntryUserId, timeEntryEngagementId, timeEntryHours, isChargeableClientEntry } from '../hooks/useTrsTimeEntries.js';
import { sampleStaffUtilization } from '../data/sampleStaffUtilizationData.js';

const axisTickStyle = { fill: 'rgb(var(--c-text-muted))', fontSize: 12 };
const tooltipStyle = {
  backgroundColor: 'rgb(var(--c-bg-surface))',
  border: '1px solid rgb(var(--c-border))',
  borderRadius: 8,
  fontSize: 12,
  color: 'rgb(var(--c-text-primary))',
};

const CHART_MAX_BARS = 10;
const PER_PAGE_OPTIONS = [10, 25, 50, 100];
const ENGAGEMENT_STATUS_LABELS = { planned: 'Planned', started: 'Started', completed: 'Completed' };

/** @param {number} pct */
function utilizationTone(pct) {
  if (pct > 105) return { label: 'Over-utilized', className: 'text-amber-500', bar: '#fbbf24' };
  if (pct < 75) return { label: 'Under-utilized', className: 'text-sky-500', bar: '#38bdf8' };
  return { label: 'Healthy', className: 'text-emerald-500', bar: '#34d399' };
}

export const StaffUtilizationReportPage = () => {
  const { activeBranchId, branches } = useBranchContext();
  const staffQuery = useStaffList(activeBranchId ? { branch_id: activeBranchId } : {});
  const engagementsQuery = useEngagements(activeBranchId ? { branch_id: activeBranchId } : {});
  const schedulesQuery = useSchedules(activeBranchId ? { branchId: activeBranchId } : {});
  const timeEntriesQuery = useTimeEntries();

  const isLoading = staffQuery.isLoading || engagementsQuery.isLoading || schedulesQuery.isLoading;
  const isError = staffQuery.isError || engagementsQuery.isError || schedulesQuery.isError;
  // TRS excluded from the gates above -- same reasoning as the other
  // pages: a TRS hiccup shouldn't take down the SSS-driven rows.
  const trsDataReady = !timeEntriesQuery.isError;

  const capacityHours = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return countWorkingDays(monthStart, monthEnd) * 8;
  }, []);

  const branchNameById = useMemo(() => new Map(branches.map((b) => [b.id, b.name])), [branches]);
  const engagementById = useMemo(
    () => new Map((engagementsQuery.data ?? []).map((e) => [String(e.id), e])),
    [engagementsQuery.data]
  );

  // Filters
  const [divisionFilter, setDivisionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_OPTIONS[0]);
  const resetToFirstPage = () => setPage(1);

  const divisionOptions = useMemo(
    () => [...new Set((engagementsQuery.data ?? []).map((e) => e.division).filter(Boolean))].sort(),
    [engagementsQuery.data]
  );
  const statusOptions = useMemo(
    () => [...new Set((engagementsQuery.data ?? []).map((e) => e.status).filter(Boolean))].sort(),
    [engagementsQuery.data]
  );

  const realRows = useMemo(() => {
    if (isLoading || isError) return [];
    const staff = staffQuery.data ?? [];
    const schedules = schedulesQuery.data ?? [];
    const chargeableEntries = trsDataReady
      ? (timeEntriesQuery.data ?? []).filter(isChargeableClientEntry)
      : [];

    /** Applies the active Division/Status filters via the entry's own engagement. */
    const passesEngagementFilter = (engId) => {
      if (!divisionFilter && !statusFilter) return true;
      const engagement = engagementById.get(engId ?? '');
      if (divisionFilter && engagement?.division !== divisionFilter) return false;
      if (statusFilter && engagement?.status !== statusFilter) return false;
      return true;
    };

    const scheduledByStaff = new Map();
    for (const s of schedules) {
      const staffId = scheduleStaffId(s);
      if (!staffId || !passesEngagementFilter(scheduleEngagementId(s))) continue;
      scheduledByStaff.set(staffId, (scheduledByStaff.get(staffId) ?? 0) + scheduleHours(s));
    }

    const actualByStaff = new Map();
    for (const t of chargeableEntries) {
      const staffId = timeEntryUserId(t);
      if (!staffId || !passesEngagementFilter(timeEntryEngagementId(t))) continue;
      actualByStaff.set(staffId, (actualByStaff.get(staffId) ?? 0) + timeEntryHours(t));
    }

    const hasFilter = Boolean(divisionFilter || statusFilter);
    return staff
      .map((s) => {
        const id = String(s.id);
        return {
          staffId: id,
          staffName: s.display_name ?? s.full_name ?? s.name ?? `Staff #${id}`,
          position: s.position ?? 'Unassigned',
          branchName: branchNameById.get(String(s.branch_id)) ?? '\u2014',
          capacityHours,
          scheduledHours: Math.round((scheduledByStaff.get(id) ?? 0) * 100) / 100,
          actualHours: Math.round((actualByStaff.get(id) ?? 0) * 100) / 100,
        };
      })
      .filter((r) => !hasFilter || scheduledByStaff.has(r.staffId) || actualByStaff.has(r.staffId));
  }, [
    isLoading, isError, trsDataReady, staffQuery.data, schedulesQuery.data, timeEntriesQuery.data,
    branchNameById, capacityHours, engagementById, divisionFilter, statusFilter,
  ]);

  // Only fall back to sample when there's a real fetch error or SSS
  // genuinely returned no staff at all -- NOT merely because the active
  // Division/Status filter happens to match zero of the real staff. A
  // filter narrowing a real (non-empty) dataset down to zero matches is
  // honest, real information ("nobody in that division this period"),
  // not something to hide behind an entirely different, fictional staff
  // list. The "No staff match the current filters" empty state below
  // handles that case correctly once this no longer masks it.
  const hasAnyRealStaff = (staffQuery.data ?? []).length > 0;
  const usingSampleData = !isLoading && (isError || !hasAnyRealStaff);
  // Sample rows always carry actualHours; real rows do too now, UNLESS
  // TRS specifically is down while SSS is fine (falls back to the
  // scheduled-hours proxy for that page load).
  const hasActualData = usingSampleData || trsDataReady;

  const sampleFilteredRows = useMemo(
    () =>
      sampleStaffUtilization.filter(
        (r) =>
          (!divisionFilter || r.primaryDivision === divisionFilter) &&
          (!statusFilter || r.primaryEngagementStatus === statusFilter)
      ),
    [divisionFilter, statusFilter]
  );

  // Sample mode's own division/status values (real mode derives options
  // from live engagements above; sample mode needs its own since there's
  // no engagements fetch backing it).
  const sampleDivisionOptions = useMemo(
    () => [...new Set(sampleStaffUtilization.map((r) => r.primaryDivision))].sort(),
    []
  );
  const sampleStatusOptions = useMemo(
    () => [...new Set(sampleStaffUtilization.map((r) => r.primaryEngagementStatus))].sort(),
    []
  );

  const rows = useMemo(() => {
    const source = usingSampleData ? sampleFilteredRows : realRows;
    return source.map((s) => ({
      ...s,
      utilizationPct: hasActualData
        ? Math.round((s.actualHours / s.capacityHours) * 1000) / 10
        : Math.round((s.scheduledHours / s.capacityHours) * 1000) / 10,
    }));
  }, [usingSampleData, sampleFilteredRows, realRows, hasActualData]);

  const avgUtilization = rows.length
    ? Math.round((rows.reduce((sum, r) => sum + r.utilizationPct, 0) / rows.length) * 10) / 10
    : 0;
  const overUtilizedCount = rows.filter((r) => r.utilizationPct > 105).length;
  const underUtilizedCount = rows.filter((r) => r.utilizationPct < 75).length;

  // Chart capped to top 10 (by utilization) and decoupled from table
  // pagination -- rendering every staff member's bar at once made the
  // chart feel sluggish; capping keeps it smooth regardless of headcount.
  const chartData = useMemo(
    () =>
      [...rows]
        .sort((a, b) => b.utilizationPct - a.utilizationPct)
        .slice(0, CHART_MAX_BARS)
        .map((r) => ({ name: r.staffName, utilization: r.utilizationPct, fill: utilizationTone(r.utilizationPct).bar })),
    [rows]
  );

  const total = rows.length;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const clampedPage = Math.min(page, lastPage);
  const pageRows = useMemo(
    () => rows.slice((clampedPage - 1) * perPage, clampedPage * perPage),
    [rows, clampedPage, perPage]
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-primary">Staff Utilization Report</h1>
          <p className="text-sm text-muted">
            {hasActualData
              ? "Actual hours logged against each staff member's capacity."
              : "Scheduled hours (SSS) against each staff member's capacity \u2014 TRS actuals pending."}
          </p>
        </div>
        {usingSampleData ? (
          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-500">
            Sample data{isError ? ' \u2014 live fetch failed' : ' \u2014 no live staff for this branch'}
          </span>
        ) : (
          <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-500">
            Live from SSS{trsDataReady ? ' & TRS' : ' \u2014 TRS unreachable, showing Scheduled Utilization'}
          </span>
        )}
      </div>

      {/* Filters -- by the engagement(s) each staff member is scheduled on */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-edge bg-surface p-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            resetToFirstPage();
          }}
          className="rounded-md border border-edge bg-base px-3 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
        >
          <option value="">All Engagement Statuses</option>
          {(usingSampleData ? sampleStatusOptions : statusOptions).map((s) => (
            <option key={s} value={s}>
              {ENGAGEMENT_STATUS_LABELS[s] ?? s}
            </option>
          ))}
        </select>

        <select
          value={divisionFilter}
          onChange={(e) => {
            setDivisionFilter(e.target.value);
            resetToFirstPage();
          }}
          className="rounded-md border border-edge bg-base px-3 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
        >
          <option value="">All Divisions</option>
          {(usingSampleData ? sampleDivisionOptions : divisionOptions).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        {(statusFilter || divisionFilter) && (
          <button
            type="button"
            onClick={() => {
              setStatusFilter('');
              setDivisionFilter('');
              resetToFirstPage();
            }}
            className="text-sm text-muted hover:text-primary"
          >
            Clear filters
          </button>
        )}

        <span className="ml-auto text-sm text-muted">{total} staff</span>
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="flex flex-1 min-w-[180px] items-center gap-3 rounded-lg border border-edge bg-surface p-4">
          <Gauge className="h-5 w-5 text-accent" />
          <div>
            <p className="text-xs text-muted">Average Utilization</p>
            <p className="text-xl font-semibold text-primary">{avgUtilization}%</p>
          </div>
        </div>
        <div className="flex flex-1 min-w-[180px] items-center gap-3 rounded-lg border border-edge bg-surface p-4">
          <TrendingUp className="h-5 w-5 text-amber-500" />
          <div>
            <p className="text-xs text-muted">Over-Utilized (&gt;105%)</p>
            <p className="text-xl font-semibold text-primary">{overUtilizedCount}</p>
          </div>
        </div>
        <div className="flex flex-1 min-w-[180px] items-center gap-3 rounded-lg border border-edge bg-surface p-4">
          <TrendingDown className="h-5 w-5 text-sky-500" />
          <div>
            <p className="text-xs text-muted">Under-Utilized (&lt;75%)</p>
            <p className="text-xl font-semibold text-primary">{underUtilizedCount}</p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-edge bg-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-medium text-primary">
            {hasActualData ? 'Utilization' : 'Scheduled Utilization'} by Staff
          </p>
          {total > CHART_MAX_BARS && (
            <span className="text-xs text-muted">Top {CHART_MAX_BARS} by utilization</span>
          )}
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--c-border))" vertical={false} />
            <XAxis dataKey="name" tick={axisTickStyle} axisLine={{ stroke: 'rgb(var(--c-border))' }} tickLine={false} />
            <YAxis tick={axisTickStyle} axisLine={false} tickLine={false} unit="%" />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgb(var(--c-bg-surface-hover))' }} formatter={(v) => `${v}%`} isAnimationActive={false} />
            <Bar dataKey="utilization" name="Utilization" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto rounded-lg border border-edge bg-surface">
        <table className="w-full min-w-[680px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-edge text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3 font-medium">Staff</th>
              <th className="px-4 py-3 font-medium">Position</th>
              <th className="px-4 py-3 font-medium">Branch</th>
              <th className="px-4 py-3 font-medium">Capacity Hrs</th>
              <th className="px-4 py-3 font-medium">{hasActualData ? 'Actual Hrs' : 'Scheduled Hrs'}</th>
              <th className="px-4 py-3 font-medium">Utilization</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => {
              const tone = utilizationTone(r.utilizationPct);
              return (
                <tr key={r.staffId} className="border-b border-edge/60 text-primary hover:bg-surface-hover">
                  <td className="px-4 py-3">{r.staffName}</td>
                  <td className="px-4 py-3 text-muted">{r.position}</td>
                  <td className="px-4 py-3 text-muted">{r.branchName}</td>
                  <td className="px-4 py-3">{r.capacityHours.toLocaleString()}</td>
                  <td className="px-4 py-3">{(hasActualData ? r.actualHours : r.scheduledHours).toLocaleString()}</td>
                  <td className={`px-4 py-3 font-medium ${tone.className}`}>
                    {r.utilizationPct}% &middot; {tone.label}
                  </td>
                </tr>
              );
            })}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted">
                  No staff match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                resetToFirstPage();
              }}
              className="rounded-md border border-edge bg-base px-2 py-1 text-sm text-primary focus:border-accent focus:outline-none"
            >
              {PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Showing page {clampedPage} of {lastPage} ({total} rows)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={clampedPage <= 1}
                onClick={() => setPage(Math.max(1, clampedPage - 1))}
                className="rounded border border-edge px-3 py-1 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={clampedPage >= lastPage}
                onClick={() => setPage(Math.min(lastPage, clampedPage + 1))}
                className="rounded border border-edge px-3 py-1 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
